'use client'

import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import Typography from 'src/components/design-system/Typography'
import ChevronDown from 'public/icons/interface/file.svg'
import ChevronSelect from 'public/icons/interface/select.svg'
import { gsap } from 'gsap'

// Porównanie bez wielkości liter i polskich znaków — „lodz” znajduje „Łódź”.
const normalizeSearchText = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .toLowerCase()
    .trim()

export const CustomSelect = forwardRef(
  (
    {
      label = '',
      options = [],
      placeholder = false,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      className = '',
      classNameList = '',
      classNameContainer = '',
      name,
      multiple = false,
      defaultValue = null,
      required = false,
      color = 'dark-blue',
      labelShow = true,
      searchable = false,
      searchPlaceholder = '',
    },
    ref,
  ) => {
    const translateEntry = useTranslateEntry()
    placeholder = placeholder ? placeholder : translateEntry('T_SELECT', false)
    const [open, setOpen] = useState(false)
    // Osobny stan od `open`: lista jest wciąż widoczna przez czas animacji zwijania.
    // Bez tego kontener traci z-index natychmiast po kliknięciu i znikająca lista
    // na ~200 ms wpada pod pola formularza leżące niżej w DOM.
    const [isListVisible, setIsListVisible] = useState(false)
    const [selectedValues, setSelectedValues] = useState(defaultValue ? (multiple ? defaultValue : [defaultValue]) : [])
    const [isValid, setIsValid] = useState(true)
    const [errorText, setErrorText] = useState('')
    const [touched, setTouched] = useState(false)
    const [query, setQuery] = useState('')
    const buttonRef = useRef(null)
    const listRef = useRef(null)
    const searchRef = useRef(null)
    const tl = useRef(null)
    const labelId = `${name}-label`
    const listboxId = `${name}-listbox`
    const errorId = `${name}-error`

    useEffect(() => {
      if (defaultValue) {
        if (multiple) setSelectedValues(defaultValue)
        else setSelectedValues([defaultValue])
      }
    }, [defaultValue, multiple])

    const validate = () => {
      if (!required) return true
      const valid = multiple ? selectedValues.length > 0 : selectedValues[0] !== undefined
      setIsValid(valid)
      setErrorText(valid ? '' : translateEntry('T_SELECT_REQUIRED'))
      return valid
    }

    useImperativeHandle(ref, () => ({
      validate,
      getValue: () => (multiple ? selectedValues : selectedValues[0]),
    }))

    useEffect(() => {
      if (touched && required) {
        validate()
      }
    }, [selectedValues, touched])

    useEffect(() => {
      if (!listRef.current) return
      if (!tl.current) {
        tl.current = gsap.timeline({
          paused: true,
          onReverseComplete: () => setIsListVisible(false),
        })
        tl.current.fromTo(
          listRef.current,
          { opacity: 0, y: -6, display: 'none' },
          {
            opacity: 1,
            y: 0,
            display: 'block',
            duration: 0.2,
            ease: 'power2.out',
          },
        )
      }

      if (open) {
        setIsListVisible(true)
        tl.current.play()
      } else {
        tl.current.reverse()
        setQuery('')
      }
    }, [open])

    const normalizedQuery = searchable ? normalizeSearchText(query) : ''
    const visibleOptions = normalizedQuery
      ? options.filter((opt) => normalizeSearchText(opt.label).includes(normalizedQuery))
      : options

    const focusFirstOption = () =>
      listRef.current?.querySelector('li[role="option"]:not([aria-disabled="true"])')?.focus()

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (listRef.current && !listRef.current.contains(e.target) && !buttonRef.current?.contains(e.target)) {
          setOpen(false)
          onBlur?.({ isValid, errorText })
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onBlur, isValid, errorText])

    const toggleOption = (optValue) => {
      if (options.find((option) => option.value === optValue)?.disabled && !selectedValues.includes(optValue)) return
      let newSelected
      if (multiple) {
        if (selectedValues.includes(optValue)) {
          newSelected = selectedValues.filter((v) => v !== optValue)
        } else {
          newSelected = [...selectedValues, optValue]
        }
      } else {
        newSelected = [optValue]
        setOpen(false)
      }
      setSelectedValues(newSelected)
      setTouched(true)

      onChange?.({
        target: {
          name: name || '',
          value: multiple ? newSelected : newSelected[0],
          isValid: required ? newSelected.length > 0 : true,
        },
      })
    }

    const handleOptionKeyDown = (e, optValue) => {
      const items = Array.from(listRef.current?.querySelectorAll('li[role="option"]:not([aria-disabled="true"])') || [])
      const index = items.findIndex((i) => i === document.activeElement)
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const next = items[index + 1] || items[0]
        next?.focus()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (searchable && index === 0) {
          searchRef.current?.focus()
          return
        }
        const prev = items[index - 1] || items[items.length - 1]
        prev?.focus()
      } else if (['Enter', ' '].includes(e.key)) {
        e.preventDefault()
        toggleOption(optValue)
        if (!multiple) buttonRef.current.focus()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
        buttonRef.current.focus()
      }
    }

    const displayText = multiple
      ? selectedValues
          .map((v) => options.find((o) => o.value === v)?.label)
          .filter(Boolean)
          .join(', ')
      : options.find((o) => o.value === selectedValues[0])?.label

    const listClassName = `${color == 'white' || color == 'journalDomain' ? '' : 'surface-dark'} absolute left-0 mt-2 w-full max-h-[500px] overflow-auto focus:outline-none z-10 ${open ? '' : 'pointer-events-none'} ${color == 'dark' ? 'bg-blueish-blueish1' : color == 'journalDomain' ? 'bg-basic-white border-1 border-basic-gray1' : color == 'white' ? 'bg-basic-white border-1 border-blueish-blueish2' : 'bg-dark-blue-dark-blue6 border-1 border-blueish-blueish2'} ${classNameList}`

    const optionItems = visibleOptions.map((opt, index) => {
      const selected = selectedValues.includes(opt.value)
      const disabled = Boolean(opt.disabled) && !selected
      return (
        <li
          key={`${opt.value}-${index}`}
          role="option"
          aria-selected={selected}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onClick={() => toggleOption(opt.value)}
          onKeyDown={(e) => handleOptionKeyDown(e, opt.value)}
          className={`${color == 'dark' ? 'body-b3-normal text-basic-white' : color == 'light' ? 'h3 h3--caps h3--bold text-basic-gray1' : color == 'white' || color == 'journalDomain' ? 'h3 h3--light' : 'body-b3-normal'} px-12 py-14 cursor-pointer ${color == 'journalDomain' ? 'hover:bg-basic-gray1/10 hover:text-basic-black' : color == 'dark' ? 'hover:bg-blueish-blueish2 hover:text-basic-white' : 'hover:bg-blueish-blueish2 hover:text-basic-gray1'} ${
            selected
              ? color == 'journalDomain'
                ? 'bg-basic-gray1 text-basic-white'
                : color == 'dark'
                  ? 'bg-blueish-blueish2 text-basic-white'
                  : 'bg-blueish-blueish2 text-basic-gray1'
              : ''
          }`}
        >
          <span className={disabled ? 'cursor-not-allowed opacity-50' : ''}>{opt.label}</span>
        </li>
      )
    })

    return (
      <div className={`relative inline-block w-full ${classNameContainer} ${isListVisible ? 'z-20' : ''}`}>
        {label != '' && (
          <label id={labelId} className={`block pb-4 ${!labelShow && 'sr-only'}`}>
            <Typography
              variant="h4"
              component="span"
              color={color == 'dark' || color == 'light' ? 'text-basic-white' : 'text-basic-black'}
            >
              {label} {required && '*'}
            </Typography>
          </label>
        )}

        <button
          ref={buttonRef}
          id={`${labelId}-button`}
          type="button"
          aria-haspopup="listbox"
          aria-labelledby={`${labelId} ${labelId}-button`}
          aria-controls={listboxId}
          aria-expanded={open}
          aria-required={required}
          aria-invalid={!isValid}
          aria-describedby={!isValid ? errorId : undefined}
          onClick={() => {
            setOpen((prev) => !prev)
            onFocus?.()
            if (searchable && !open) {
              setTimeout(() => searchRef.current?.focus(), 50)
            }
          }}
          onKeyDown={(e) => {
            onKeyDown?.(e)
            if (['ArrowDown', 'Enter', ' '].includes(e.key)) {
              e.preventDefault()
              setOpen(true)
              setTimeout(() => (searchable ? searchRef.current?.focus() : focusFirstOption()), 50)
            } else if (e.key === 'Escape') {
              setOpen(false)
            }
          }}
          className={`flex justify-between items-center gap-8 w-full ${color == 'light-blue' ? "text-dark-blue-dark-blue2 h3 h3--caps h3--bold h-48 relative after:bg-style1 after:content-[''] after:w-full after:h-4 after:block after:absolute after:left-0 after:bottom-0" : color == 'dark' ? 'h-48 px-12 bg-blueish-blueish1 text-basic-white body-b3-normal' : color == 'light' ? "text-basic-white h3 h3--caps h3--bold h-48 relative after:bg-style1 after:content-[''] after:w-full after:h-4 after:block after:absolute after:left-0 after:bottom-0" : color == 'journalDomain' ? 'pl-6 bg-basic-white border-b-1 border-b-basic-gray1 h-44 text-basic-black h3 h3--light' : color == 'white' ? 'pl-6 bg-basic-white border-b-1 border-b-dark-blue-dark-blue3 h-44 text-basic-black h3 h3--light' : 'body-b3-normal text-basic-black px-12 bg-dark-blue-dark-blue6 border-b border-b-dark-blue-dark-blue3 h-44'} ${className}`}
        >
          <span className="min-w-0 flex-1 truncate text-left" title={displayText || undefined}>
            {displayText || placeholder}
          </span>
          {color == 'dark' || color == 'light' ? (
            <ChevronDown
              className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          ) : (
            <ChevronSelect
              className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          )}
        </button>

        {/* Lista przed komunikatem błędu — jest absolutna bez `top`, więc jej punkt zaczepienia
            wyznacza pozycja statyczna. Gdyby stała po <p>, pojawienie się błędu zsuwałoby ją w dół. */}
        {searchable ? (
          // Pole wyszukiwania nie może siedzieć w role="listbox", więc animowany kontener
          // (listRef) obejmuje input i listę, a listbox zostaje samym <ul>.
          <div ref={listRef} data-lenis-prevent className={listClassName} style={{ display: 'none', opacity: 0 }}>
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  focusFirstOption()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setOpen(false)
                  buttonRef.current?.focus()
                }
              }}
              placeholder={searchPlaceholder || translateEntry('T_SEARCH_BTN')}
              aria-label={searchPlaceholder || translateEntry('T_SEARCH_BTN')}
              aria-controls={listboxId}
              autoComplete="off"
              className="sticky top-0 z-[1] w-full border-b-1 border-b-blueish-blueish2 bg-basic-white px-12 py-12 text-basic-black h3 h3--light outline-none"
            />
            <ul id={listboxId} role="listbox" aria-multiselectable={multiple} aria-labelledby={labelId}>
              {optionItems}
              {visibleOptions.length === 0 && (
                <li role="presentation" className="px-12 py-14 h3 h3--light text-basic-gray4">
                  {translateEntry('T_NO_RESULTS')}
                </li>
              )}
            </ul>
          </div>
        ) : (
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            data-lenis-prevent
            aria-multiselectable={multiple}
            aria-labelledby={labelId}
            className={listClassName}
            style={{ display: 'none', opacity: 0 }}
          >
            {optionItems}
          </ul>
        )}

        {errorText && (
          <p id={errorId} className="mt-6" role="alert">
            <Typography variant="body2" component="span" color="text-functional-error-red2">
              {errorText}
            </Typography>
          </p>
        )}
      </div>
    )
  },
)
CustomSelect.displayName = 'CustomSelect'
